'use client';

import { c as _c } from "react/compiler-runtime";
import { checkPlatformContent, CONNECTABLE_PLATFORMS, type ConnectablePlatform, getContentType, localUploadMeta, PLATFORM_LABELS, platformTextWarning, shortUnsupportedReason, X_PREMIUM_TEXT_WARNING_SHORT } from '@shared/all/crosspost';
import type { CrossPostPlatform } from '@shared/all/validators';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { Globe, X as XIcon } from 'lucide-react';
import Link from 'next/link';
import { type CSSProperties, type ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PLATFORM_BRAND } from '../../../utils/platformBrand';
import { client } from '../../../utils/trpc';
import { useNewPostContext } from './NewPostContext';
const POPUP_W = 300;
const MARGIN = 8;
// Grace period for the pointer to travel from the globe into the popup.
const CLOSE_DELAY_MS = 200;
const SETTINGS_HREF = '/settings/connected-apps';

// datetime-local speaks local wall-clock, so toISOString (UTC) would shift the
// value by the viewer's offset. Build the string from the local getters instead.
const toLocalInputValue = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
type Row = {
  platform: ConnectablePlatform;
  label: string;
  // Connected and healthy: the row is a switch. Otherwise it only explains.
  toggleable: boolean;
  selected: boolean;
  limited: boolean;
  // Why the post can't go out as it stands, e.g. a video over the limit.
  problem: string | null;
  // May still go out, but fails on some accounts, e.g. long text on free X.
  warning: string | null;
};
const CrossPostIndicator = () => {
  const $ = _c(74);
  const {
    uploads,
    content,
    parentPostId,
    isPrivate,
    isBoardPost,
    crossPostSelection,
    setCrossPostSelection,
    crossPostScheduledFor,
    setCrossPostScheduledFor
  } = useNewPostContext();
  const [anchorRect, setAnchorRect] = useState(null);
  const buttonRef = useRef(null);
  const closeTimer = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["autoCrossPostSettings"],
      queryFn: _temp,
      retry: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: autoSettings
  } = useQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["connectedPlatforms"],
      queryFn: _temp2,
      retry: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: connectedData
  } = useQuery(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["platformHealth"],
      queryFn: _temp3,
      retry: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: health
  } = useQuery(t2);
  if (parentPostId || isPrivate) {
    return null;
  }
  let enabledCount;
  let openPopup;
  let popup;
  let scheduleClose;
  if ($[3] !== anchorRect || $[4] !== autoSettings?.boardCards || $[5] !== autoSettings?.platforms || $[6] !== connectedData || $[7] !== content || $[8] !== crossPostScheduledFor || $[9] !== crossPostSelection || $[10] !== health?.platforms || $[11] !== isBoardPost || $[12] !== setCrossPostScheduledFor || $[13] !== setCrossPostSelection || $[14] !== uploads) {
    const autoPlatforms = new Set(autoSettings?.platforms ?? []);
    let t3;
    if ($[19] !== connectedData?.connected) {
      t3 = connectedData?.connected ?? [];
      $[19] = connectedData?.connected;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    let t4;
    if ($[21] !== t3) {
      t4 = new Set(t3);
      $[21] = t3;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    const connectedPlatforms = t4;
    let t5;
    if ($[23] !== health?.platforms) {
      t5 = platform => {
        const env = health?.platforms?.[platform];
        return env !== "sandbox" && env !== "broken";
      };
      $[23] = health?.platforms;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    const isHealthy = t5;
    let t6;
    if ($[25] !== connectedPlatforms || $[26] !== isHealthy) {
      t6 = p => connectedPlatforms.has(p) && isHealthy(p);
      $[25] = connectedPlatforms;
      $[26] = isHealthy;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    const availablePlatforms = CONNECTABLE_PLATFORMS.filter(t6);
    const available = new Set(availablePlatforms);
    const autoApplies = !isBoardPost || (autoSettings?.boardCards ?? false);
    const selected = new Set((crossPostSelection ?? (autoApplies ? [...autoPlatforms] : [])).filter(p_0 => available.has(p_0)));
    enabledCount = selected.size;
    const allSelected = availablePlatforms.length > 0 && availablePlatforms.every(p_1 => selected.has(p_1));
    const togglePlatform = platform_0 => {
      const next = new Set(selected);
      if (next.has(platform_0)) {
        next.delete(platform_0);
      } else {
        next.add(platform_0);
      }
      setCrossPostSelection(CONNECTABLE_PLATFORMS.filter(p_2 => next.has(p_2)));
    };
    let t7;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => {
        if (closeTimer.current) {
          clearTimeout(closeTimer.current);
        }
      };
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    const cancelClose = t7;
    let t8;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = () => {
        cancelClose();
        if (buttonRef.current) {
          setAnchorRect(buttonRef.current.getBoundingClientRect());
        }
      };
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    openPopup = t8;
    let t9;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setAnchorRect(null), CLOSE_DELAY_MS);
      };
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    scheduleClose = t9;
    const uploadMeta = uploads.filter(_temp4).map(_temp5);
    const contentType = getContentType(uploadMeta);
    const rows = CONNECTABLE_PLATFORMS.map(platform_1 => {
      const limited = !isHealthy(platform_1);
      const toggleable = connectedPlatforms.has(platform_1) && !limited;
      const isSelected = selected.has(platform_1);
      let problem = null;
      let warning = null;
      if (isSelected) {
        const check = checkPlatformContent(platform_1, uploadMeta, content.length);
        if (!check.ok) {
          problem = check.issues.length > 0 ? check.issues[0].message : shortUnsupportedReason(contentType);
        } else {
          if (platformTextWarning(platform_1, content.length)) {
            warning = X_PREMIUM_TEXT_WARNING_SHORT;
          }
        }
      }
      return {
        platform: platform_1,
        label: PLATFORM_LABELS[platform_1],
        toggleable,
        selected: isSelected,
        limited,
        problem,
        warning
      };
    });
    const active = rows.filter(_temp6);
    const unconnected = rows.filter(_temp7);
    popup = null;
    if (anchorRect && typeof window !== "undefined") {
      const left = Math.min(Math.max(MARGIN, anchorRect.left + anchorRect.width / 2 - POPUP_W / 2), window.innerWidth - POPUP_W - MARGIN);
      const placeBelow = anchorRect.top < 320 && window.innerHeight - anchorRect.bottom > anchorRect.top;
      let t10;
      if ($[31] !== anchorRect.bottom || $[32] !== anchorRect.top || $[33] !== placeBelow) {
        t10 = placeBelow ? {
          top: anchorRect.bottom + MARGIN
        } : {
          bottom: window.innerHeight - anchorRect.top + MARGIN
        };
        $[31] = anchorRect.bottom;
        $[32] = anchorRect.top;
        $[33] = placeBelow;
        $[34] = t10;
      } else {
        t10 = $[34];
      }
      let t11;
      if ($[35] !== left || $[36] !== t10) {
        t11 = {
          position: "fixed",
          width: POPUP_W,
          zIndex: 400,
          left,
          ...t10
        };
        $[35] = left;
        $[36] = t10;
        $[37] = t11;
      } else {
        t11 = $[37];
      }
      const style = t11;
      let t12;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = <p className="text-sm font-emphasis text-textPrimary">Cross-post</p>;
        $[38] = t12;
      } else {
        t12 = $[38];
      }
      let t13;
      if ($[39] !== availablePlatforms || $[40] !== enabledCount) {
        t13 = availablePlatforms.length > 0 && <p className="text-xs text-textSecondary/70">{enabledCount} of {availablePlatforms.length}</p>;
        $[39] = availablePlatforms;
        $[40] = enabledCount;
        $[41] = t13;
      } else {
        t13 = $[41];
      }
      let t14;
      if ($[42] !== t13) {
        t14 = <div className="flex items-baseline justify-between px-4 pb-1 pt-3">{t12}{t13}</div>;
        $[42] = t13;
        $[43] = t14;
      } else {
        t14 = $[43];
      }
      const t15 = connectedData === undefined ? <p className="px-4 pb-4 pt-2 text-xs text-textSecondary/70">Checking…</p> : active.length === 0 ? <div className="px-4 pb-4 pt-2"><p className="text-xs text-textSecondary">No networks connected yet. Connect one and every post can go there too.</p><Link href={SETTINGS_HREF} className="mt-3 block rounded-full border border-ds-border-standard bg-background-light py-1.5 text-center text-sm font-emphasis text-textPrimary no-underline hover:bg-ds-border-hover">Connect a network</Link></div> : <div className="px-2 pb-2">{active.map(row => {
          const brand = PLATFORM_BRAND[row.platform];
          const inner = <><span className={`flex size-7 shrink-0 items-center justify-center rounded-md ${brand.bg} ${brand.color}`}><brand.icon size={15} /></span><span className="min-w-0 flex-1 text-left"><span className="block text-sm text-textPrimary">{row.label}</span>{(row.problem ?? row.warning) && <span className="block text-[11px] text-amber-500">{row.problem ?? row.warning}</span>}</span></>;
          if (row.limited) {
            return <div key={row.platform} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 opacity-70">{inner}<span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ds-border-standard px-2 py-px text-[11px] font-emphasis text-textSecondary"><span className="size-1.5 rounded-full bg-amber-500" />Limited</span></div>;
          }
          return <button key={row.platform} type="button" role="switch" aria-checked={row.selected} onClick={() => togglePlatform(row.platform)} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-background-light">{inner}<span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${row.selected ? "bg-primary" : "bg-textSecondary/30"}`}><span className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform duration-200 ${row.selected ? "translate-x-4" : "translate-x-0"}`} /></span></button>;
        })}</div>;
      const t16 = active.length > 0 && unconnected.length > 0 && <p className="border-t border-ds-border-subtle px-4 py-2 text-[11px] text-textSecondary/70">Not connected: {unconnected.map(_temp8).join(", ")}.{" "}<Link href={SETTINGS_HREF} className="text-primary no-underline hover:underline">Connect</Link></p>;
      let t17;
      if ($[44] !== connectedPlatforms.size || $[45] !== crossPostScheduledFor || $[46] !== setCrossPostScheduledFor) {
        t17 = connectedPlatforms.size > 0 && <div className="border-t border-ds-border-subtle px-4 py-2"><label htmlFor="crosspost-schedule" className="mb-1 block text-[11px] text-textSecondary/70">{crossPostScheduledFor ? "Cross-posts at" : "Cross-posts with the post"}</label><div className="flex items-center gap-1"><input id="crosspost-schedule" type="datetime-local" min={toLocalInputValue(new Date())} value={crossPostScheduledFor ? toLocalInputValue(crossPostScheduledFor) : ""} onChange={e => setCrossPostScheduledFor(e.target.value ? new Date(e.target.value) : null)} className="min-w-0 flex-1 rounded-md border border-ds-border-standard bg-background-light px-2 py-1 text-xs text-textPrimary" />{crossPostScheduledFor && <button type="button" title="Cross-post with the post instead" onClick={() => setCrossPostScheduledFor(null)} className="shrink-0 rounded p-1 text-textSecondary hover:bg-background-light"><XIcon size={12} /></button>}</div></div>;
        $[44] = connectedPlatforms.size;
        $[45] = crossPostScheduledFor;
        $[46] = setCrossPostScheduledFor;
        $[47] = t17;
      } else {
        t17 = $[47];
      }
      let t18;
      if ($[48] !== allSelected || $[49] !== availablePlatforms || $[50] !== setCrossPostSelection) {
        t18 = availablePlatforms.length > 1 && <div className="flex items-center justify-between border-t border-ds-border-subtle px-4 py-2 text-xs"><button type="button" onClick={() => setCrossPostSelection(allSelected ? [] : availablePlatforms)} className="text-textSecondary hover:text-textPrimary">{allSelected ? "Clear all" : "Select all"}</button><Link href={SETTINGS_HREF} className="text-textSecondary/70 no-underline hover:text-textPrimary">Defaults</Link></div>;
        $[48] = allSelected;
        $[49] = availablePlatforms;
        $[50] = setCrossPostSelection;
        $[51] = t18;
      } else {
        t18 = $[51];
      }
      let t19;
      if ($[52] !== style || $[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t17 || $[57] !== t18) {
        t19 = createPortal(<div style={style} onMouseEnter={cancelClose} onMouseLeave={scheduleClose} className="overflow-hidden rounded-xl border border-ds-border-standard bg-background-primary shadow-lg">{t14}{t15}{t16}{t17}{t18}</div>, document.body);
        $[52] = style;
        $[53] = t14;
        $[54] = t15;
        $[55] = t16;
        $[56] = t17;
        $[57] = t18;
        $[58] = t19;
      } else {
        t19 = $[58];
      }
      popup = t19;
    }
    $[3] = anchorRect;
    $[4] = autoSettings?.boardCards;
    $[5] = autoSettings?.platforms;
    $[6] = connectedData;
    $[7] = content;
    $[8] = crossPostScheduledFor;
    $[9] = crossPostSelection;
    $[10] = health?.platforms;
    $[11] = isBoardPost;
    $[12] = setCrossPostScheduledFor;
    $[13] = setCrossPostSelection;
    $[14] = uploads;
    $[15] = enabledCount;
    $[16] = openPopup;
    $[17] = popup;
    $[18] = scheduleClose;
  } else {
    enabledCount = $[15];
    openPopup = $[16];
    popup = $[17];
    scheduleClose = $[18];
  }
  const t3 = `flex size-9 shrink-0 items-center justify-center rounded text-textSecondary hover:bg-background-light ${enabledCount > 0 ? "text-primary" : ""}`;
  let t4;
  if ($[59] !== anchorRect || $[60] !== openPopup) {
    t4 = () => anchorRect ? setAnchorRect(null) : openPopup();
    $[59] = anchorRect;
    $[60] = openPopup;
    $[61] = t4;
  } else {
    t4 = $[61];
  }
  let t5;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Globe size={28} className="h-7 w-7" />;
    $[62] = t5;
  } else {
    t5 = $[62];
  }
  let t6;
  if ($[63] !== enabledCount) {
    t6 = enabledCount > 0 && <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">{enabledCount}</span>;
    $[63] = enabledCount;
    $[64] = t6;
  } else {
    t6 = $[64];
  }
  let t7;
  if ($[65] !== openPopup || $[66] !== scheduleClose || $[67] !== t3 || $[68] !== t4 || $[69] !== t6) {
    t7 = <button ref={buttonRef} type="button" title="Choose where to cross-post" className={t3} onClick={t4} onMouseEnter={openPopup} onMouseLeave={scheduleClose}>{t5}{t6}</button>;
    $[65] = openPopup;
    $[66] = scheduleClose;
    $[67] = t3;
    $[68] = t4;
    $[69] = t6;
    $[70] = t7;
  } else {
    t7 = $[70];
  }
  let t8;
  if ($[71] !== popup || $[72] !== t7) {
    t8 = <div className="relative">{t7}{popup}</div>;
    $[71] = popup;
    $[72] = t7;
    $[73] = t8;
  } else {
    t8 = $[73];
  }
  return t8;
};
export default CrossPostIndicator;
function _temp() {
  return unwrapFromResult(client.crosspost.getAutoCrossPostSettings.query());
}
function _temp2() {
  return unwrapFromResult(client.crosspost.getConnectedPlatforms.query());
}
function _temp3() {
  return unwrapFromResult(client.crosspost.getPlatformHealth.query());
}
function _temp4(u) {
  return u.isVideo || !u.mimeType || u.mimeType.startsWith("image/");
}
function _temp5(u_0) {
  return localUploadMeta({
    isVideo: u_0.isVideo,
    sizeBytes: u_0.sizeBytes,
    mimeType: u_0.mimeType,
    durationSec: u_0.durationSec,
    width: u_0.width ?? null,
    height: u_0.height ?? null
  });
}
function _temp6(r) {
  return r.toggleable || r.limited;
}
function _temp7(r_0) {
  return !r_0.toggleable && !r_0.limited;
}
function _temp8(r_1) {
  return r_1.label;
}